import {
  DEFAULT_ASSIGNMENT_FILTERS,
  assignmentFilterConfig,
  type AssignmentFilters,
} from '@/components/filters';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { getEmployees } from '@/lib/employees';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, UserPlus, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { PickerFilterSheet } from './filter-sheet';
import type { AvailableEmployeesPanelProps } from './types';
import { useCheckboxSelection } from './use-checkbox-selection';

// ============================================================================
// Utils
// ============================================================================

function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

// ============================================================================
// Component
// ============================================================================

export function AvailableEmployeesPanel({
  excludeEmployeeIds,
  onAddEmployees,
  disabled = false,
}: Omit<AvailableEmployeesPanelProps, 'excludeDepartmentIds' | 'onAddDepartment'>) {
  // Ref for search input to maintain focus
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter state
  const [filters, setFilters] = useState<AssignmentFilters>(DEFAULT_ASSIGNMENT_FILTERS);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Build query params from filters
  const queryParams = useMemo(() => {
    const params = assignmentFilterConfig.toQueryParams?.(filters) ?? {};
    return {
      ...params,
      search: debouncedSearch || undefined,
      status: 'active' as const,
      limit: 100,
      page: 1,
    };
  }, [filters, debouncedSearch]);

  // Fetch employees
  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ['employees', 'picker', queryParams],
    queryFn: () => getEmployees(queryParams),
  });

  // Filter out already selected employees
  const availableEmployees = useMemo(() => {
    if (!employeesData?.data) return [];
    return employeesData.data.filter((emp) => !excludeEmployeeIds.has(emp.id));
  }, [employeesData?.data, excludeEmployeeIds]);

  // Checkbox selection hook
  const selection = useCheckboxSelection(availableEmployees);

  // Get all checked employees (regardless of current filter)
  const checkedEmployeesList = selection.getCheckedEmployeesList();
  const checkedIdsSet = new Set(checkedEmployeesList.map((e) => e.id));

  // Filter out checked employees from available list
  const unselectedEmployees = useMemo(() => {
    return availableEmployees.filter((emp) => !checkedIdsSet.has(emp.id));
  }, [availableEmployees, checkedIdsSet]);

  const visibleIds = unselectedEmployees.map((e) => e.id);

  // Handle add selected employees
  const handleAddSelected = () => {
    const employees = selection.getCheckedEmployees();
    if (employees.length > 0) {
      onAddEmployees(employees);
      selection.uncheckAll();
    }
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.departmentIds.length > 0) count++;
    if (filters.positionIds.length > 0) count++;
    if (filters.seniorityLevelIds.length > 0) count++;
    if (filters.divisionIds.length > 0) count++;
    if (filters.locationIds.length > 0) count++;
    if (filters.employmentTypes.length > 0) count++;
    return count;
  }, [filters]);

  // Clear all filters
  const handleClearFilters = () => {
    setFilters(DEFAULT_ASSIGNMENT_FILTERS);
    setSearchQuery('');
  };

  return (
    <div className="flex flex-col h-full border rounded-xl bg-card overflow-hidden">
      {/* Header with Search and Filter Button */}
      <div className="p-3 border-b space-y-3">
        {/* Search Row */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
              disabled={disabled}
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <PickerFilterSheet
            filters={filters}
            onChange={setFilters}
            activeCount={activeFilterCount}
            onClear={handleClearFilters}
          />
        </div>
      </div>

      {/* Selection Controls */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <Checkbox
            id="select-all"
            checked={
              visibleIds.length === 0
                ? false
                : selection.areAllVisibleChecked(visibleIds)
                  ? true
                  : selection.areSomeVisibleChecked(visibleIds)
                    ? 'indeterminate'
                    : false
            }
            onCheckedChange={() => selection.toggleAllVisible(visibleIds)}
            disabled={disabled || unselectedEmployees.length === 0}
            className="h-4 w-4"
          />
          <label
            htmlFor="select-all"
            className="text-sm text-muted-foreground cursor-pointer select-none"
          >
            Select all
            <span className="ml-1 opacity-70">({unselectedEmployees.length})</span>
          </label>
        </div>

        <Button
          size="sm"
          onClick={handleAddSelected}
          disabled={disabled || selection.checkedCount === 0}
          className="gap-1.5 h-8"
        >
          <UserPlus className="h-4 w-4" />
          Add{selection.checkedCount > 0 && ` (${selection.checkedCount})`}
        </Button>
      </div>

      {/* Employee List */}
      <ScrollArea className="flex-1 min-h-0">
        {employeesLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : checkedEmployeesList.length === 0 && unselectedEmployees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <Search className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              {debouncedSearch || activeFilterCount > 0
                ? 'No employees match filters'
                : 'No available employees'}
            </p>
            {(debouncedSearch || activeFilterCount > 0) && (
              <Button
                variant="link"
                size="sm"
                className="mt-1 h-auto p-0 text-xs"
                onClick={handleClearFilters}
              >
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <div className="p-1">
            {/* Selected Employees Section */}
            {checkedEmployeesList.length > 0 && (
              <>
                <div className="px-2 py-1.5 mb-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Selected ({checkedEmployeesList.length})
                  </p>
                </div>
                {checkedEmployeesList.map((employee) => (
                  <EmployeeRow
                    key={employee.id}
                    employee={employee}
                    isChecked={true}
                    onToggle={() => selection.toggle(employee.id)}
                    disabled={disabled}
                    onRestoreFocus={() => {
                      setTimeout(() => searchInputRef.current?.focus(), 0);
                    }}
                  />
                ))}
                {unselectedEmployees.length > 0 && (
                  <div className="px-2 py-1.5 mt-2 mb-1 border-t">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Available ({unselectedEmployees.length})
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Unselected Employees */}
            {unselectedEmployees.map((employee) => (
              <EmployeeRow
                key={employee.id}
                employee={employee}
                isChecked={false}
                onToggle={() => selection.toggle(employee.id)}
                disabled={disabled}
                onRestoreFocus={() => {
                  setTimeout(() => searchInputRef.current?.focus(), 0);
                }}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Footer */}
      {!employeesLoading && employeesData && employeesData.meta.total > 0 && (
        <div className="px-3 py-2 border-t bg-muted/20">
          <p className="text-xs text-muted-foreground text-center">
            {availableEmployees.length} of {employeesData.meta.total} available
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EmployeeRow Component
// ============================================================================

interface EmployeeRowProps {
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string | null;
    avatarUrl?: string | null;
    workEmail: string;
    position?: { title: string } | null;
    department?: { name: string } | null;
  };
  isChecked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  onRestoreFocus?: () => void;
}

function EmployeeRow({
  employee,
  isChecked,
  onToggle,
  disabled,
  onRestoreFocus,
}: EmployeeRowProps) {
  const formatName = useEmployeeNameFormatter();
  const displayName = formatName(employee);

  const handleToggle = () => {
    onToggle();
    // Restore focus to search input after toggle
    onRestoreFocus?.();
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer transition-colors',
        'hover:bg-muted/50',
        isChecked && 'bg-primary/5 hover:bg-primary/10'
      )}
      onClick={(e) => {
        // Only handle click if not clicking on checkbox
        if ((e.target as HTMLElement).closest('[role="checkbox"]') === null) {
          handleToggle();
        }
      }}
    >
      <Checkbox
        checked={isChecked}
        onCheckedChange={handleToggle}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          // Prevent focus loss
          e.preventDefault();
        }}
        onMouseDown={(e) => {
          // Prevent focus loss on mouse down
          e.preventDefault();
        }}
        className="h-4 w-4"
      />
      <Avatar className="h-8 w-8">
        {employee.avatarUrl && <AvatarImage src={employee.avatarUrl} />}
        <AvatarFallback className="text-xs font-medium">
          {getInitials(employee.firstName, employee.lastName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{displayName}</div>
        <div className="text-xs text-muted-foreground truncate">
          {employee.position?.title ?? 'No position'}
          {employee.department?.name && ` · ${employee.department.name}`}
        </div>
      </div>
    </div>
  );
}
